package com.islamicmedia.site.pages

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.coroutines.await
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.varabyte.kobweb.core.Page

// ─── Models ───────────────────────────────────────────────────────────────────

private val seedJson = Json { ignoreUnknownKeys = true; isLenient = true }

@Serializable
private data class SeedPhase1Payload(
    val contentId: String? = null,
    val cacheHit: Boolean = false,
    val result: SeedMinResult? = null
)

@Serializable
private data class SeedMinResult(
    val score: Int? = null,
    @SerialName("islamicScore") val islamicScore: Int? = null,
    val summary: String? = null
)

// ─── Data ─────────────────────────────────────────────────────────────────────

private data class CatalogEntry(
    val title: String,
    val mediaType: String,
    val year: String? = null,
    val section: String
)

private enum class EntryStatus { Pending, Running, Done, Error }

private data class EntryState(
    val entry: CatalogEntry,
    val status: EntryStatus = EntryStatus.Pending,
    val score: Int? = null,
    val islamicScore: Int? = null,
    val cacheHit: Boolean = false,
    val error: String? = null
)

private val CATALOG: List<CatalogEntry> = listOf(
    // ── Movies — Animated ─────────────────────────────────────────────────────
    CatalogEntry("Bilal: A New Breed of Hero", "MOVIES", "2015", "Movies — Animated"),
    CatalogEntry("Muhammad: The Last Prophet", "MOVIES", "2002", "Movies — Animated"),
    CatalogEntry("The Boy and the King", "MOVIES", null, "Movies — Animated"),
    CatalogEntry("The Adventures of Ibn Battuta", "MOVIES", null, "Movies — Animated"),
    CatalogEntry("Saladin", "MOVIES", null, "Movies — Animated"),
    CatalogEntry("The Story of Prophet Ibrahim", "MOVIES", null, "Movies — Animated"),
    CatalogEntry("The Jar: A Tale From the East", "MOVIES", null, "Movies — Animated"),
    CatalogEntry("Habil & Qabil", "MOVIES", null, "Movies — Animated"),
    // ── Movies — Live Action ──────────────────────────────────────────────────
    CatalogEntry("The Message", "MOVIES", "1976", "Movies — Live Action"),
    CatalogEntry("Lion of the Desert", "MOVIES", "1980", "Movies — Live Action"),
    CatalogEntry("Muhammad: The Messenger of God", "MOVIES", "2015", "Movies — Live Action"),
    CatalogEntry("The Kingdom of Solomon", "MOVIES", "2010", "Movies — Live Action"),
    CatalogEntry("The Messiah", "MOVIES", "2007", "Movies — Live Action"),
    CatalogEntry("Uwais al-Qarni", "MOVIES", "2007", "Movies — Live Action"),
    CatalogEntry("Conquest 1453", "MOVIES", "2012", "Movies — Live Action"),
    CatalogEntry("Kingdom of Heaven", "MOVIES", "2005", "Movies — Live Action"),
    CatalogEntry("Journey to Mecca", "MOVIES", "2009", "Movies — Live Action"),
    CatalogEntry("Malcolm X", "MOVIES", "1992", "Movies — Live Action"),
    CatalogEntry("Mooz-Lum", "MOVIES", "2010", "Movies — Live Action"),
    CatalogEntry("My Name is Khan", "MOVIES", "2010", "Movies — Live Action"),
    CatalogEntry("The Swimmers", "MOVIES", "2022", "Movies — Live Action"),
    CatalogEntry("Farha", "MOVIES", "2021", "Movies — Live Action"),
    // ── Movies — Documentary ──────────────────────────────────────────────────
    CatalogEntry("Islam: Empire of Faith", "MOVIES", "2000", "Movies — Documentary"),
    CatalogEntry("Inside Mecca", "MOVIES", "2003", "Movies — Documentary"),
    CatalogEntry("When the Moors Ruled in Europe", "MOVIES", "2005", "Movies — Documentary"),
    CatalogEntry("An Islamic History of Europe", "MOVIES", "2005", "Movies — Documentary"),
    CatalogEntry("Cities of Light: The Rise and Fall of Islamic Spain", "MOVIES", "2007", "Movies — Documentary"),
    CatalogEntry("Prince Among Slaves", "MOVIES", "2007", "Movies — Documentary"),
    CatalogEntry("Inside Islam", "MOVIES", "2009", "Movies — Documentary"),
    CatalogEntry("1001 Inventions", "MOVIES", "2010", "Movies — Documentary"),
    CatalogEntry("Islamic Art: Mirror of the Invisible World", "MOVIES", "2012", "Movies — Documentary"),
    CatalogEntry("One Day in the Haram", "MOVIES", "2017", "Movies — Documentary"),
    CatalogEntry("Koran by Heart", "MOVIES", null, "Movies — Documentary"),
    CatalogEntry("Hajj: The Journey of a Lifetime", "MOVIES", "2001", "Movies — Documentary"),
    // ── TV — Turkish Historical ───────────────────────────────────────────────
    CatalogEntry("Resurrection: Ertugrul", "TV_SHOWS", "2014", "TV — Turkish Historical"),
    CatalogEntry("Establishment: Osman", "TV_SHOWS", "2019", "TV — Turkish Historical"),
    CatalogEntry("Awakening: Great Seljuk", "TV_SHOWS", "2020", "TV — Turkish Historical"),
    CatalogEntry("Alparslan: The Great Seljuks", "TV_SHOWS", "2021", "TV — Turkish Historical"),
    CatalogEntry("Barbarossa: Sword of the Mediterranean", "TV_SHOWS", "2021", "TV — Turkish Historical"),
    CatalogEntry("Mehmed: Sultan of Conquests", "TV_SHOWS", "2024", "TV — Turkish Historical"),
    CatalogEntry("Saladin: The Conqueror of Jerusalem", "TV_SHOWS", "2023", "TV — Turkish Historical"),
    CatalogEntry("Payitaht: Abdulhamid", "TV_SHOWS", "2017", "TV — Turkish Historical"),
    CatalogEntry("Yunus Emre: Journey of Love", "TV_SHOWS", "2015", "TV — Turkish Historical"),
    CatalogEntry("Hay Sultan", "TV_SHOWS", "2023", "TV — Turkish Historical"),
    CatalogEntry("Mevlana Rumi", "TV_SHOWS", "2023", "TV — Turkish Historical"),
    CatalogEntry("Mavera", "TV_SHOWS", "2021", "TV — Turkish Historical"),
    CatalogEntry("Celaleddin: Lion of the Steppe", "TV_SHOWS", "2021", "TV — Turkish Historical"),
    CatalogEntry("El Turco", "TV_SHOWS", "2025", "TV — Turkish Historical"),
    CatalogEntry("Mehmetcik Kutulamare", "TV_SHOWS", "2018", "TV — Turkish Historical"),
    CatalogEntry("Rise of Empires: Ottoman", "TV_SHOWS", "2020", "TV — Turkish Historical"),
    CatalogEntry("Rise of Empires: Mehmet vs Vlad", "TV_SHOWS", "2022", "TV — Turkish Historical"),
    // ── TV — Arabic Historical ────────────────────────────────────────────────
    CatalogEntry("Omar", "TV_SHOWS", "2012", "TV — Arabic Historical"),
    CatalogEntry("Khalid ibn al-Walid", "TV_SHOWS", "2006", "TV — Arabic Historical"),
    CatalogEntry("Salah ad-Din al-Ayyubi", "TV_SHOWS", "2001", "TV — Arabic Historical"),
    CatalogEntry("Men Around the Prophet", "TV_SHOWS", "2006", "TV — Arabic Historical"),
    CatalogEntry("Al-Zahir Baybars", "TV_SHOWS", "2005", "TV — Arabic Historical"),
    CatalogEntry("The Imam", "TV_SHOWS", "2017", "TV — Arabic Historical"),
    CatalogEntry("Khaybar", "TV_SHOWS", "2013", "TV — Arabic Historical"),
    CatalogEntry("Al-Hassan and Al-Hussain", "TV_SHOWS", "2011", "TV — Arabic Historical"),
    CatalogEntry("Abu Hanifa al-Numan", "TV_SHOWS", "1997", "TV — Arabic Historical"),
    // ── TV — Iranian/Persian ──────────────────────────────────────────────────
    CatalogEntry("Prophet Joseph", "TV_SHOWS", "2008", "TV — Iranian/Persian"),
    CatalogEntry("Imam Ali", "TV_SHOWS", "1997", "TV — Iranian/Persian"),
    CatalogEntry("Mokhtarnameh", "TV_SHOWS", "2010", "TV — Iranian/Persian"),
    CatalogEntry("Saint Mary", "TV_SHOWS", "2002", "TV — Iranian/Persian"),
    CatalogEntry("Salman the Persian", "TV_SHOWS", "2024", "TV — Iranian/Persian"),
    CatalogEntry("Ashaab e Kahaf", "TV_SHOWS", null, "TV — Iranian/Persian"),
    // ── TV — Contemporary ─────────────────────────────────────────────────────
    CatalogEntry("Little Mosque on the Prairie", "TV_SHOWS", "2007", "TV — Contemporary"),
    CatalogEntry("All-American Muslim", "TV_SHOWS", "2011", "TV — Contemporary"),
    CatalogEntry("Ramy", "TV_SHOWS", "2019", "TV — Contemporary"),
    CatalogEntry("Al-Rawabi School for Girls", "TV_SHOWS", "2021", "TV — Contemporary"),
    CatalogEntry("Alif", "TV_SHOWS", "2019", "TV — Contemporary"),
    // ── TV — Kids Animated ────────────────────────────────────────────────────
    CatalogEntry("Omar & Hana", "TV_SHOWS", "2017", "TV — Kids Animated"),
    CatalogEntry("Zaky & Friends", "TV_SHOWS", "2010", "TV — Kids Animated"),
    CatalogEntry("Barakah Hills", "TV_SHOWS", "2018", "TV — Kids Animated"),
    CatalogEntry("Upin & Ipin", "TV_SHOWS", "2007", "TV — Kids Animated"),
    CatalogEntry("Stories of the Prophets", "TV_SHOWS", "2020", "TV — Kids Animated"),
    CatalogEntry("Ali and Sumaya: Let's Pray!", "TV_SHOWS", "2009", "TV — Kids Animated"),
    CatalogEntry("Little Ammar", "TV_SHOWS", "2022", "TV — Kids Animated"),
    CatalogEntry("Adam's World", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("Laith & Layla", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("I'm The Best Muslim", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("Misri Bunch", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("The Adventures of Salahuddin", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("Saladin: The Animated Series", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("Ibn Battuta: The Animated Series", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("Quran Stories for Kids", "TV_SHOWS", null, "TV — Kids Animated"),
    CatalogEntry("Animal Stories from Quran", "TV_SHOWS", null, "TV — Kids Animated"),
    // ── YouTube — Kids & Family ───────────────────────────────────────────────
    CatalogEntry("Omar & Hana", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Muslim Kids TV", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("IQRA Cartoon", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("One4Kids", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Muslim Reminder Kids", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Mini Muslim", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Super Muslim Kids", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("The Little Believers", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Zain Bhikha Kids", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Little Muslims", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Funu Kids", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("The Prophets Path", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Ali Huda", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    CatalogEntry("Iqra Islamic Academy", "YOUTUBE_CHANNELS", null, "YouTube — Kids & Family"),
    // ── YouTube — Islamic Education ───────────────────────────────────────────
    CatalogEntry("Mufti Menk", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("MercifulServant", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("Yaqeen Institute", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("OnePath Network", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("FreeQuranEducation", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("Mohammed Hijab", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("Ali Dawah", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("The Thinking Muslim", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("iERA", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("Muslim Central", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    CatalogEntry("Stories of the Prophets Quran Stories", "YOUTUBE_CHANNELS", null, "YouTube — Islamic Education"),
    // ── Apps ──────────────────────────────────────────────────────────────────
    CatalogEntry("Noor Kids", "APPS", null, "Apps"),
    CatalogEntry("Ali and Sumaya", "APPS", null, "Apps"),
    CatalogEntry("Omar and Hana", "APPS", null, "Apps"),
    CatalogEntry("Muslim Kids TV", "APPS", null, "Apps"),
    CatalogEntry("Ali Huda", "APPS", null, "Apps"),
    CatalogEntry("One4Kids TV", "APPS", null, "Apps"),
    CatalogEntry("Islamic Quiz", "APPS", null, "Apps"),
    CatalogEntry("Deen Quiz", "APPS", null, "Apps"),
    CatalogEntry("Learns Quran with Marbel", "APPS", null, "Apps"),
    CatalogEntry("Daily Duas for Kids", "APPS", null, "Apps"),
    CatalogEntry("Muslim Kids Educational Games", "APPS", null, "Apps"),
    CatalogEntry("Noorani Qaida", "APPS", null, "Apps"),
    CatalogEntry("Hakma Muslim Kids Reader", "APPS", null, "Apps"),
    CatalogEntry("Miraj Muslim Kids", "APPS", null, "Apps"),
    CatalogEntry("10 Surah for Kids Word By Word", "APPS", null, "Apps"),
    CatalogEntry("Muslims Game Islamic Ramadan", "APPS", null, "Apps"),
)

// ─── Page ─────────────────────────────────────────────────────────────────────

@Page("/admin/seed")
@Composable
fun SeedCatalogPage() {
    val scope = rememberCoroutineScope()
    val states = remember { mutableStateListOf(*CATALOG.map { EntryState(it) }.toTypedArray()) }
    var job by remember { mutableStateOf<Job?>(null) }

    val isRunning = job?.isActive == true
    val total = states.size
    val done = states.count { it.status == EntryStatus.Done || it.status == EntryStatus.Error }
    val green = states.count { (it.score ?: 0) >= 8 && it.score != null }
    val yellow = states.count { val s = it.score ?: 0; s in 5..7 }
    val red = states.count { val s = it.score ?: 0; s in 1..4 }
    val cached = states.count { it.cacheHit }

    // Group indices by section, preserving catalog order
    val sectionGroups: Map<String, List<Int>> = linkedMapOf<String, MutableList<Int>>().also { map ->
        states.indices.forEach { i -> map.getOrPut(states[i].entry.section) { mutableListOf() }.add(i) }
    }

    Div(attrs = {
        style {
            property("font-family", "monospace, 'Courier New', Courier")
            backgroundColor(Color("#0d0d1a"))
            color(Color("#e0e0e0"))
            minHeight(100.vh)
            padding(28.px)
            boxSizing("border-box")
        }
    }) {
        // ── Title ──────────────────────────────────────────────────────────────
        H2(attrs = { style { color(Color("#a78bfa")); margin(0.px); fontSize(20.px) } }) {
            Text("Catalog Seed — Quick Check")
        }
        P(attrs = { style { color(Color("#666")); margin(4.px, 0.px, 16.px, 0.px); fontSize(13.px) } }) {
            Text("Runs phase-1 (quick check) on every title. Results are cached in the DB.")
        }

        // ── Stats ──────────────────────────────────────────────────────────────
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(24.px)
                fontSize(13.px)
                marginBottom(12.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            StatChip("Total", "$total", "#888")
            StatChip("Done", "$done", "#a78bfa")
            StatChip("Green ●", "$green", "#4ade80")
            StatChip("Yellow ●", "$yellow", "#fbbf24")
            StatChip("Red ●", "$red", "#f87171")
            StatChip("Cached", "$cached", "#60a5fa")
        }

        // ── Progress bar ───────────────────────────────────────────────────────
        Div(attrs = {
            style {
                width(100.percent)
                height(6.px)
                backgroundColor(Color("#1e1e2e"))
                borderRadius(3.px)
                marginBottom(16.px)
                property("overflow", "hidden")
            }
        }) {
            Div(attrs = {
                style {
                    width(if (total > 0) (done.toFloat() / total * 100).percent else 0.percent)
                    height(100.percent)
                    backgroundColor(Color("#a78bfa"))
                    borderRadius(3.px)
                    property("transition", "width 0.3s ease")
                }
            })
        }

        // ── Controls ───────────────────────────────────────────────────────────
        Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); marginBottom(24.px) } }) {
            SeedButton(
                label = if (isRunning) "⏹ Stop" else "▶ Run All",
                bgColor = if (isRunning) "#ef4444" else "#7c3aed"
            ) {
                if (isRunning) {
                    job?.cancel()
                    for (i in states.indices) {
                        if (states[i].status == EntryStatus.Running)
                            states[i] = states[i].copy(status = EntryStatus.Pending)
                    }
                } else {
                    job = scope.launch {
                        val serverUrl = "https://fitrah-filter-dev.fly.dev"
                        for (i in states.indices) {
                            if (states[i].status == EntryStatus.Done) continue
                            states[i] = states[i].copy(status = EntryStatus.Running, error = null)
                            try {
                                val payload = fetchQuickCheck(serverUrl, states[i].entry)
                                states[i] = states[i].copy(
                                    status = EntryStatus.Done,
                                    score = payload?.result?.score,
                                    islamicScore = payload?.result?.islamicScore,
                                    cacheHit = payload?.cacheHit ?: false
                                )
                            } catch (e: CancellationException) {
                                states[i] = states[i].copy(status = EntryStatus.Pending)
                                throw e
                            } catch (e: Exception) {
                                states[i] = states[i].copy(
                                    status = EntryStatus.Error,
                                    error = e.message?.take(80) ?: "Failed"
                                )
                                delay(1000) // back off on error
                            }
                            delay(400)
                        }
                    }
                }
            }
            SeedButton("Reset Errors", "#374151") {
                for (i in states.indices) {
                    if (states[i].status == EntryStatus.Error)
                        states[i] = states[i].copy(status = EntryStatus.Pending, error = null)
                }
            }
            SeedButton("Reset All", "#374151") {
                job?.cancel()
                for (i in states.indices) {
                    states[i] = EntryState(states[i].entry)
                }
            }
        }

        // ── Results table ──────────────────────────────────────────────────────
        for ((sectionName, indices) in sectionGroups) {
            val sectionDone = indices.count { states[it].status == EntryStatus.Done }
            H3(attrs = {
                style {
                    color(Color("#a78bfa"))
                    margin(28.px, 0.px, 6.px, 0.px)
                    fontSize(11.px)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "1.5px")
                }
            }) {
                Text("$sectionName  ($sectionDone/${indices.size})")
            }
            Table(attrs = {
                style {
                    width(100.percent)
                    property("border-collapse", "collapse")
                    fontSize(13.px)
                    marginBottom(4.px)
                }
            }) {
                Thead {
                    Tr {
                        SeedTh("Title", align = "left", width = null)
                        SeedTh("Year", align = "center", width = "56px")
                        SeedTh("Status", align = "center", width = "110px")
                        SeedTh("Score", align = "center", width = "54px")
                        SeedTh("Islamic", align = "center", width = "64px")
                        SeedTh("Verdict", align = "center", width = "90px")
                    }
                }
                Tbody {
                    for (i in indices) {
                        val st = states[i]
                        val rowBg = when {
                            st.score != null && st.score >= 7 -> "rgba(74,222,128,0.07)"
                            st.score != null && st.score >= 4 -> "rgba(251,191,36,0.07)"
                            st.score != null             -> "rgba(248,113,113,0.07)"
                            else -> "transparent"
                        }
                        Tr(attrs = {
                            style {
                                backgroundColor(Color(rowBg))
                                property("border-bottom", "1px solid #1a1a2e")
                            }
                        }) {
                            // Title + error
                            Td(attrs = { style { padding(6.px, 8.px) } }) {
                                Text(st.entry.title)
                                if (st.cacheHit) Span(attrs = { style { color(Color("#60a5fa")); fontSize(11.px); marginLeft(6.px) } }) { Text("⚡cached") }
                                if (st.error != null) Div(attrs = { style { color(Color("#f87171")); fontSize(11.px); marginTop(2.px) } }) { Text(st.error) }
                            }
                            // Year
                            Td(attrs = { style { textAlign("center"); padding(6.px, 8.px); color(Color("#555")) } }) {
                                Text(st.entry.year ?: "—")
                            }
                            // Status
                            Td(attrs = { style { textAlign("center"); padding(6.px, 8.px) } }) {
                                val (label, color) = when (st.status) {
                                    EntryStatus.Pending -> "—" to "#555"
                                    EntryStatus.Running -> "⚡ running" to "#fbbf24"
                                    EntryStatus.Done    -> "✓ done" to "#4ade80"
                                    EntryStatus.Error   -> "✗ error" to "#f87171"
                                }
                                Span(attrs = { style { color(Color(color)) } }) { Text(label) }
                            }
                            // Score
                            Td(attrs = {
                                style {
                                    textAlign("center"); padding(6.px, 8.px)
                                    fontWeight("bold")
                                    fontSize(15.px)
                                    color(Color(when {
                                        st.score == null    -> "#555"
                                        st.score >= 7       -> "#4ade80"
                                        st.score >= 4       -> "#fbbf24"
                                        else                -> "#f87171"
                                    }))
                                }
                            }) { Text(st.score?.toString() ?: "—") }
                            // Islamic score
                            Td(attrs = { style { textAlign("center"); padding(6.px, 8.px); color(Color("#60a5fa")) } }) {
                                Text(st.islamicScore?.toString() ?: "—")
                            }
                            // Verdict chip
                            Td(attrs = { style { textAlign("center"); padding(6.px, 8.px) } }) {
                                val (verdict, chip) = when {
                                    st.score == null -> "" to ""
                                    st.score >= 7    -> "Green" to "#4ade80"
                                    st.score >= 4    -> "Yellow" to "#fbbf24"
                                    else             -> "Red" to "#f87171"
                                }
                                if (verdict.isNotEmpty()) {
                                    Span(attrs = {
                                        style {
                                            color(Color(chip))
                                            backgroundColor(Color(chip.replace("#", "#1a") + "22"))
                                            padding(2.px, 8.px)
                                            borderRadius(12.px)
                                            fontSize(11.px)
                                            property("font-weight", "600")
                                        }
                                    }) { Text("● $verdict") }
                                } else {
                                    Text("—")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ─── Helper Composables ───────────────────────────────────────────────────────

@Composable
private fun StatChip(label: String, value: String, color: String) {
    Div(attrs = {
        style {
            backgroundColor(Color("#1a1a2e"))
            borderRadius(6.px)
            padding(6.px, 12.px)
            display(DisplayStyle.Flex)
            gap(6.px)
            alignItems(AlignItems.Center)
        }
    }) {
        Span(attrs = { style { color(Color(color)); fontSize(13.px) } }) { Text(label) }
        Span(attrs = { style { color(Color("#e0e0e0")); property("font-weight", "700") } }) { Text(value) }
    }
}

@Composable
private fun SeedButton(label: String, bgColor: String, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            backgroundColor(Color(bgColor))
            color(Color("white"))
            border(0.px)
            borderRadius(6.px)
            padding(8.px, 18.px)
            property("cursor", "pointer")
            fontSize(13.px)
            property("font-weight", "600")
            property("transition", "opacity 0.15s")
        }
    }) { Text(label) }
}

@Composable
private fun SeedTh(text: String, align: String, width: String?) {
    Th(attrs = {
        style {
            textAlign(align)
            padding(6.px, 8.px)
            color(Color("#555"))
            property("font-weight", "normal")
            fontSize(11.px)
            property("text-transform", "uppercase")
            property("letter-spacing", "0.8px")
            if (width != null) property("width", width)
        }
    }) { Text(text) }
}

// ─── API ──────────────────────────────────────────────────────────────────────

private suspend fun fetchQuickCheck(serverUrl: String, entry: CatalogEntry): SeedPhase1Payload? {
    val bodyJson = buildJsonObject {
        put("title", entry.title)
        put("mediaType", entry.mediaType)
        entry.year?.let { put("year", it) }
        put("quick_check", true)
        put("criteria", buildJsonObject {
            put("customConcerns", "")
            put("childAgeRanges", buildJsonArray { })
        })
    }.toString()

    val headers = js("({'Content-Type': 'application/json'})")
    val init = kotlin.js.json(
        "method" to "POST",
        "headers" to headers,
        "body" to bodyJson
    ).unsafeCast<org.w3c.fetch.RequestInit>()

    val response = window.fetch("$serverUrl/api/analyze", init).await()
    if (!response.ok) throw Exception("HTTP ${response.status}: ${response.statusText}")
    val text = response.text().await()
    return parsePhase1FromSSE(text)
}

private fun parsePhase1FromSSE(sseText: String): SeedPhase1Payload? {
    var currentEvent = ""
    for (line in sseText.lines()) {
        when {
            line.startsWith("event:") -> currentEvent = line.removePrefix("event:").trim()
            line.startsWith("data:") && currentEvent == "phase1_complete" -> {
                return try {
                    seedJson.decodeFromString<SeedPhase1Payload>(line.removePrefix("data:").trim())
                } catch (_: Exception) { null }
            }
            line.startsWith("data:") && currentEvent == "error" -> {
                val msg = try {
                    val el = seedJson.parseToJsonElement(line.removePrefix("data:").trim())
                    el.jsonObject["message"]?.jsonPrimitive?.content ?: "Analysis failed"
                } catch (_: Exception) { "Analysis failed" }
                throw Exception(msg)
            }
        }
    }
    return null
}
